import React, { PureComponent } from 'react';
import ReactDOM from 'react-dom';
import scrollIntoView from 'scroll-into-view-if-needed';
import classnames from 'classnames';
import { translate } from 'react-i18next';

import { ButtonTypes, ButtonWithPopover } from 'common/components/core/button';
import {
    PopoverPlaceEnum,
    PopoverTriggerEnum
} from 'common/components/core/popover/popOverFactory';
import { listCardFactory } from 'common/components/core/card/ListCard';
import ConfirmationDialog from 'common/components/core/dialog/ConfirmationDialog';
import ZoneDeleteDialog from 'common/components/zoneMap/ZoneDeleteDialog';
import VerifyButton from 'common/entity/generic/VerifyButton';
import theme from './ZoneMapGeometryCard.scss';
import compose from 'recompose/compose';
import withStateHandlers from 'recompose/withStateHandlers';
import { withHandlers } from 'recompose';
import { zoneMapColors } from 'common/enums/ColorEnum';
import EventUtils from 'common/utils/EventUtils';
import testId from 'common/tests/masterManagement/mapMaster.json';

const ExtendedRightIcons = ({
    t,
    theme,
    onVerify,
    onHide,
    handleDeleteClick,
    isVerified,
    isVerifyDisabled,
    isVerifyHidden,
    isHidden,
    isDragging
}) => {
    let verifyPopover;
    if (isVerifyDisabled) {
        verifyPopover = t('zoneMap.noVerifyAccess');
    } else {
        verifyPopover = t(
            isVerified ? 'zoneMap.unverifyZone' : 'zoneMap.verifyZone'
        );
    }
    return !isDragging ? (
        <div className={theme.rightIcons}>
            <ButtonWithPopover
                className={theme.viewGroupBtn}
                icon={isHidden ? 'eye-hide' : 'eye'}
                onClick={onHide}
                invertPopover
                popOverContent={!isHidden ? t('zoneMap.hideZone') : ''}
                place={PopoverPlaceEnum.Above}
                triggerPopoverOn={PopoverTriggerEnum.Hover}
                buttonType={ButtonTypes.Icon}
                testId={testId.map.hideZoneBtn}
            />

            <ButtonWithPopover
                className={theme.deleteBtn}
                icon="delete-bin"
                onClick={handleDeleteClick}
                invertPopover
                popOverContent={t('zoneMap.deleteZone')}
                place={PopoverPlaceEnum.Above}
                triggerPopoverOn={PopoverTriggerEnum.Hover}
                buttonType={ButtonTypes.Icon}
                testId={testId.map.deleteZoneBtn}
            />

            {!isVerifyHidden ? (
                <VerifyButton
                    theme={theme}
                    disabled={isVerifyDisabled}
                    isVerified={isVerified}
                    onVerify={onVerify}
                    noLabel
                    popOver={verifyPopover}
                    testId={testId.map.verifyZoneBtn}
                />
            ) : null}
        </div>
    ) : null;
};

const GeometryTitleWithRouteId = ({ theme, title, routeId }) => {
    return (
        <div className={theme.titleWrap}>
            <div className={theme.title} testId={testId.map.sidebarZoneTitle}>
                {title}
            </div>
            {routeId ? (
                <div
                    className={theme.routeId}
                    testId={testId.map.sidebarZoneId}
                >
                    {routeId}
                </div>
            ) : null}
        </div>
    );
};

const GeometryCard = listCardFactory({
    Title: GeometryTitleWithRouteId,
    RightIcon: ExtendedRightIcons
});

class ZoneMapGeometryCard extends PureComponent {
    componentDidUpdate(prevProps) {
        const { isSelected } = this.props;
        if (isSelected && !prevProps.isSelected) {
            const element = ReactDOM.findDOMNode(this);
            scrollIntoView(element, {
                scrollMode: 'if-needed',
                block: 'nearest',
                behavior: 'smooth'
            });
        }
    }
    onMouseEnter = () => {
        const { geometry, handleGeometryCardHover } = this.props;
        handleGeometryCardHover({ geometry });
    };

    onMouseLeave = () => {
        const { resetHoveredGeometries } = this.props;
        resetHoveredGeometries();
    };

    handleClick = () => {
        const { geometry, onClick, onHide } = this.props;
        const clickHandler = !geometry.isHidden() ? onClick : onHide;
        clickHandler({ geometry });
    };

    render() {
        const {
            t,
            geometry,
            className,
            isHidden,
            isExpanded,
            isSelected,
            isDragging,
            isHoveredDuringDrag,
            isUnverifyDialogActive,
            isDeleteDialogActive,
            onVerifyToggle,
            isVerifyDisabled,
            setUnverifyDialogInactive,
            setDeleteDialogInactive,
            handleHide,
            handleDeleteClick,
            onDelete,
            resetFolder
        } = this.props;
        const { name, routeId, description } = geometry;
        const isVerified = geometry.isVerified();
        const isGrouped = !!geometry.directoryId;
        const { color } = geometry.getStyles();
        const [selectedColor] = Object.values(zoneMapColors).filter(
            dashboardColor => dashboardColor.code === color
        );
        const colorName = selectedColor ? selectedColor.name : 'dodger-blue';
        const isVerifyHidden = isVerifyDisabled && !isVerified;
        return (
            <div className={theme.geometryCardWrap}>
                <GeometryCard
                    t={t}
                    isVerified={isVerified}
                    isGrouped={isGrouped}
                    isDragging={isDragging}
                    title={name}
                    routeId={routeId}
                    subtitle={description}
                    leftIcon="zone"
                    leftIconColor={colorName}
                    theme={theme}
                    isSelected={isSelected}
                    onClick={this.handleClick}
                    onHide={handleHide}
                    onVerify={onVerifyToggle}
                    handleDeleteClick={handleDeleteClick}
                    isVerifyHidden={isVerifyHidden}
                    isVerifyDisabled={isVerifyDisabled}
                    isHidden={isHidden}
                    onMouseEnter={this.onMouseEnter}
                    onMouseLeave={this.onMouseLeave}
                    className={classnames(theme.geometryCard, className, {
                        [theme.expanded]: isExpanded,
                        [theme.hidden]: isHidden,
                        [theme.dragging]: isDragging,
                        [theme.dragBoundary]: isHoveredDuringDrag
                    })}
                    resetFolder={resetFolder}
                    testId={testId.map.zoneGeometryCard}
                />

                {isUnverifyDialogActive ? (
                    <ConfirmationDialog
                        theme={theme}
                        active={isUnverifyDialogActive}
                        onAccept={onVerifyToggle}
                        onCancel={setUnverifyDialogInactive}
                        title={t('zoneMap.unverifyZone')}
                        innerText={t('zoneMap.unverifyZoneWarning')}
                        acceptActionLabel={t('zoneMap.unverify')}
                    />
                ) : null}
                {isDeleteDialogActive ? (
                    <ZoneDeleteDialog
                        active={isDeleteDialogActive}
                        onAccept={onDelete}
                        onCancel={setDeleteDialogInactive}
                    />
                ) : null}
            </div>
        );
    }
}

const ZoneMapGeometryCardWithHandlers = compose(
    withStateHandlers(
        () => {
            return {
                isUnverifyDialogActive: false,
                isDeleteDialogActive: false
            };
        },
        {
            setUnverifyDialogActive: () => () => {
                return { isUnverifyDialogActive: true };
            },
            setUnverifyDialogInactive: () => () => {
                return { isUnverifyDialogActive: false };
            },
            setDeleteDialogActive: () => () => {
                return { isDeleteDialogActive: true };
            },
            setDeleteDialogInactive: () => () => {
                return { isDeleteDialogActive: false };
            }
        }
    ),
    withHandlers({
        onVerifyToggle: ({
            geometry,
            isMapVerified,
            isUnverifyDialogActive,
            setUnverifyDialogActive,
            setUnverifyDialogInactive,
            handleZoneVerifyToggle
        }) => e => {
            const verified = geometry.isVerified();
            EventUtils.stopPropagation(e);
            if (verified && isMapVerified && !isUnverifyDialogActive) {
                setUnverifyDialogActive();
                return;
            }
            handleZoneVerifyToggle({ geometry });
            setUnverifyDialogInactive();
        },
        handleHide: ({ geometry, onHide }) => e => {
            EventUtils.stopPropagation(e);
            onHide({ geometry });
        },
        onDelete: ({
            geometry,
            handleZoneDelete,
            setDeleteDialogInactive
        }) => e => {
            EventUtils.stopPropagation(e);
            handleZoneDelete({ zoneId: geometry.id });
            setDeleteDialogInactive();
        },

        resetFolder: ({ ungroupZone, geometry }) => e => {
            EventUtils.stopPropagation(e);
            ungroupZone({ zone: geometry });
        }
    }),
    withHandlers({
        handleDeleteClick: ({
            geometry,
            setDeleteDialogActive,
            onDelete
        }) => e => {
            EventUtils.stopPropagation(e);
            geometry.isVerified() ? setDeleteDialogActive() : onDelete();
        }
    })
)(ZoneMapGeometryCard);
export default translate('entityManagement')(ZoneMapGeometryCardWithHandlers);
